import React, { useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Divider,
  Typography,
  Button,
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  Snackbar,
  Alert
} from "@mui/material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import type { Product, CartItem, Payment, Customer, RepairTicket } from "../types/pos";
import POSMiniTopBarShortcuts from "../components/POSMiniTopBarShortcuts";
import ProductSearch from "../components/ProductSearch";
import CartPanel from "../components/CartPanel";
import CustomerEntryPanel from "../components/CustomerEntryPanel";
import PaymentPanel from "../components/PaymentPanel";
import CartBasket from "../components/CartBasket";
import TicketLinker from "../components/TicketLinker";
const RecentSalesPanel = React.lazy(() => import("../components/RecentSalesPanel"));

const POSDashboard: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [notes, setNotes] = useState("");
  const [ticket, setTicket] = useState<RepairTicket | null>(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const handleAddProduct = (product: Product) => {
    const exists = cart.find((item) => item._id === product._id);
    if (exists) {
      setCart(cart.map(item =>
        item._id === product._id
          ? { ...item, quantity: item.quantity + 1, lineTotal: (item.quantity + 1) * (item.sellingPrice ?? item.price) }
          : item
      ));
    } else {
      setCart([
        ...cart,
        {
          ...product,
          quantity: 1,
          lineTotal: product.sellingPrice ?? product.price,
        },
      ]);
    }
  };

  const getWalkInCustomer = () => {
    if (!customer) return undefined;
    if ((customer as any)._id) return undefined;
    const name = (customer as any).name || (customer as any).fullName || "";
    if (!name && !customer.phone) return undefined;
    return { name, phone: customer.phone };
  };

  const prepareSaleItems = () =>
    cart.map(item => ({
      type: (item as any).type || "part",
      itemId: item._id,
      name: item.name,
      quantity: item.quantity,
      unitPrice: item.sellingPrice ?? item.price,
      taxRate: item.taxRate ?? 0,
      discount: item.discount ?? 0,
      lineTotal:
        ((item.sellingPrice ?? item.price) * item.quantity - (item.discount ?? 0)) *
        (1 + (item.taxRate ?? 0) / 100),
    }));

  const subtotal = cart.reduce((sum, item) => sum + (item.sellingPrice ?? item.price) * item.quantity, 0);
  const discountTotal = cart.reduce((sum, item) => sum + (item.discount ?? 0), 0);
  const taxTotal = cart.reduce(
    (sum, item) =>
      sum +
      (((item.sellingPrice ?? item.price) * item.quantity - (item.discount ?? 0)) *
        ((item.taxRate ?? 0) / 100)),
    0
  );
  const grandTotal = subtotal - discountTotal + taxTotal;

  const handleCheckout = async () => {
    if (!cart.length) return;
    setLoading(true);
    try {
      await api.post(
        "/sales",
        {
          items: prepareSaleItems(),
          clientId: (customer as any)?._id,
          walkInCustomer: getWalkInCustomer(),
          payments: payments || [],
          notes,
          ticketId: ticket?._id,
          subtotal,
          taxTotal,
          discountTotal,
          total: grandTotal,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart([]);
      setCustomer(null);
      setPayments([]);
      setNotes("");
      setTicket(null);
      navigate("/pos");
    } catch (err: any) {
      alert(err?.response?.data?.message || "Sale failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleShortcut = (action: string) => {
    switch (action) {
      case "newSale":
        setCart([]);
        setCustomer(null);
        setPayments([]);
        setNotes("");
        setTicket(null);
        setSnack("New sale started");
        break;
      case "hold":
        // Save current cart as a held order in localStorage
        try {
          const heldRaw = localStorage.getItem("pos:heldOrders");
          const held = heldRaw ? JSON.parse(heldRaw) : [];
          const order = {
            id: `H-${Date.now()}`,
            at: new Date().toISOString(),
            cart,
            customer,
            payments,
            notes,
            ticket,
          };
          localStorage.setItem("pos:heldOrders", JSON.stringify([order, ...held].slice(0, 20)));
          setSnack("Order held");
        } catch { /* no-op */ }
        break;
      case "recall":
        try {
          const heldRaw = localStorage.getItem("pos:heldOrders");
          const held = heldRaw ? JSON.parse(heldRaw) : [];
          if (!held.length) {
            setSnack("No held orders");
            break;
          }
          const order = held.shift();
          localStorage.setItem("pos:heldOrders", JSON.stringify(held));
          setCart(order.cart || []);
          setCustomer(order.customer || null);
          setPayments(order.payments || []);
          setNotes(order.notes || "");
          setTicket(order.ticket || null);
          setSnack(`Recalled ${order.id}`);
        } catch { setSnack("Failed to recall order"); }
        break;
      case "printLast":
        (async () => {
          try {
            const res = await api.get("/pos", { headers: { Authorization: `Bearer ${token}` } });
            const sales = Array.isArray(res.data) ? res.data : [];
            if (!sales.length) return setSnack("No recent sales to print");
            const last = sales[0];
            navigate(`/pos/sale/${last._id}`);
          } catch { setSnack("Failed to load last sale"); }
        })();
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ mb: 2 }}>
        <POSMiniTopBarShortcuts onShortcut={handleShortcut} />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={8.5}>
          <Card sx={{ position: "relative", borderRadius: 3 }}>
            <CardHeader title="Point of Sale" subheader="Search, add to cart, capture customer and payments" />
            <CardContent>
              <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                <CartBasket cart={cart} onClick={() => window.scrollTo({ top: 300, behavior: "smooth" })} />
              </Box>
              <Stack spacing={2}>
                <TicketLinker value={ticket} onChange={setTicket} />
                <ProductSearch onSelect={handleAddProduct} />
                <Divider />
                <CartPanel cart={cart} setCart={setCart} />
                <Divider />
                <CustomerEntryPanel customer={customer} setCustomer={setCustomer} />
                <Divider />
                <PaymentPanel payments={payments} setPayments={setPayments} total={grandTotal} ticket={ticket} />
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Sale Notes (optional)</Typography>
                  <TextField multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes, delivery instructions, etc." />
                </Stack>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button variant="contained" size="large" startIcon={<ShoppingCartCheckoutIcon />} disabled={cart.length === 0 || payments.length === 0 || loading} onClick={handleCheckout}>
                    {loading ? "Processing..." : "Complete Sale"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} lg={3.5}>
          <Stack spacing={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader title="Recent Sales" />
              <CardContent>
                <Suspense fallback={<Typography variant="body2" color="text.secondary">Loading…</Typography>}>
                  <RecentSalesPanel />
                </Suspense>
              </CardContent>
            </Card>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button fullWidth variant="outlined" startIcon={<AssessmentIcon />} onClick={() => navigate("/pos/reports")}>Sales Reports</Button>
              <Button fullWidth variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate("/settings/pos")}>POS Settings</Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
        <Alert severity="info" onClose={() => setSnack(null)} sx={{ width: '100%' }}>
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSDashboard;

